import React, { useEffect, useState } from 'react';
import { AttendanceStats } from '../../../api/API';
import './StreakDashboardCard.css';

interface StreakDashboardCardProps {
    attendanceStats: AttendanceStats;
}

const StreakDashboardCard: React.FC<StreakDashboardCardProps> = ({ attendanceStats }) => {
    const { currentStreak, longestStreak, nextMilestoneWeeks, currentWeekMessage } = attendanceStats;
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const [offset, setOffset] = useState(circumference);

    const percent = (() => {
        if (currentStreak > 0 && nextMilestoneWeeks) {
            return Math.min(100, Math.round((currentStreak / nextMilestoneWeeks) * 100));
        }
        if (currentStreak > 0 && !nextMilestoneWeeks) return 100;
        return 0;
    })();

    useEffect(() => {
        if (nextMilestoneWeeks && currentStreak > 0) {
            const progress = currentStreak / nextMilestoneWeeks;
            setOffset(circumference - Math.min(progress, 1) * circumference);
        } else if (currentStreak > 0 && !nextMilestoneWeeks) {
            setOffset(0);
        } else {
            setOffset(circumference);
        }
    }, [currentStreak, nextMilestoneWeeks, circumference]);

    const isActive = currentStreak > 0;

    return (
        <div className={`streak-dashboard-card ${isActive ? 'streak-active' : 'streak-inactive'}`}>
            <div className="streak-card-content">
                <div className="streak-circle-container">
                    <svg className="streak-circle-svg" viewBox="0 0 100 100">
                        <circle
                            className="streak-circle-bg"
                            cx="50"
                            cy="50"
                            r={radius}
                        />
                        {isActive && (
                            <circle
                                className="streak-circle-progress"
                                cx="50"
                                cy="50"
                                r={radius}
                                style={{ strokeDasharray: circumference, strokeDashoffset: offset }}
                            />
                        )}
                    </svg>
                    <div className="streak-circle-inner">
                        {isActive ? (
                            <span className="streak-number">
                                {nextMilestoneWeeks ? `${percent}%` : '✓'}
                            </span>
                        ) : (
                            <span className="streak-number-zero">0</span>
                        )}
                    </div>
                </div>

                <div className="streak-info-column">
                    {isActive ? (
                        <>
                            <h2 className="streak-title">
                                {currentStreak} Week Dance Streak
                            </h2>
                            {currentWeekMessage ? (
                                <div
                                    className="streak-subtitle"
                                    dangerouslySetInnerHTML={{ __html: currentWeekMessage }}
                                />
                            ) : (
                                <p className="streak-subtitle">
                                    You've danced {currentStreak} week{currentStreak !== 1 ? 's' : ''} in a row. Keep it flowing.
                                </p>
                            )}
                            {longestStreak > currentStreak && (
                                <p className="streak-longest">Longest Streak: {longestStreak} weeks</p>
                            )}
                        </>
                    ) : (
                        <>
                            <h2 className="streak-title streak-title-inactive">Start your Dance Streak</h2>
                            <p className="streak-subtitle">
                                Attend one class this week and your Streak begins.
                            </p>
                            {longestStreak > 0 && (
                                <p className="streak-longest">Longest Streak: {longestStreak} weeks</p>
                            )}
                        </>
                    )}
                </div>
            </div>

        </div>
    );
};

export default StreakDashboardCard;
